import React, { useState } from 'react';
import Head from 'next/head';

import Redirect from 'legacy/components/util/Redirect/Redirect';
import FadeIn from 'legacy/common/components/transition/FadeIn';

import useViewAssociation from './hooks/useViewAssociation';
import DesktopView from '../../components/view-org-layout/DesktopView';
import MoreOptionsModal from './components/MoreOptionsModal';
import Header from '../../components/view-org-layout/Header';
import ShareModal from '../../components/view-org-layout/ShareModal';
import MobileView from '../../components/view-org-layout/MobileView';
import config from '../../../config';
import EmailDomainModal from './components/EmailDomainModal';
import useAuthenticationStatus from 'legacy/common/hooks/useAuthenticationStatus';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

const ViewAssociation = () => {
  const { isAuthenticated } = useAuthenticationStatus();
  const isLoggedIn = isAuthenticated();
  const { t } = useTranslation(['associations']);
  const {
    data: {
      association,
      isLoading,
      isError,
      isDesktop,
      isEmailDomainModalVisible,
      isJoinBlocked
    },
    joinAssociation,
    closeEmailDomainModal
  } = useViewAssociation();

  const blockedMessage = isJoinBlocked
    ? t('publicView.joinBlocked')
    : undefined;
  const [isMoreOptionsModalOpen, setIsMoreOptionsModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const toggleMoreOptionsModal = () => {
    setIsMoreOptionsModalOpen((moreOptionsOpen) => !moreOptionsOpen);
  };

  const toggleShareModal = () => {
    setIsShareModalOpen((shareModalOpen) => !shareModalOpen);
  };

  if (isError) {
    return <Redirect to="/404" />;
  }

  if (isLoading || !association) {
    return null;
  }

  return (
    <>
      <Head>
        <title>{association.name || 'Association'} | Bounce</title>
      </Head>
      {isLoggedIn && (
        <Header
          toggleShareModal={toggleShareModal}
          toggleMoreOptions={toggleMoreOptionsModal}
          orgName={association.name}
          iconUrl={association.logoImage}
          title={t('title')}
          isDesktop={isDesktop}
          isUserMember={association.isUserMember}
          isUserPending={association.isUserPending}
          isUserAdmin={association.isUserAdmin}
          join={joinAssociation}
        />
      )}
      <div
        className={classNames('md:-mt-[10px]', {
          '-mb-[60px] -mt-[90px]': isLoggedIn // see comment in organizations/components/ViewOrganization/index.tsx
        })}
      >
        <FadeIn>
          {isDesktop ? (
            <DesktopView
              toggleShareModal={toggleShareModal}
              name={association.name}
              memberCount={association.memberCount}
              memberPreview={association.memberPreview}
              badgeUrl={association.logoImage}
              coverUrl={association.coverImage}
              description={association.description}
              isUserAdmin={association.isUserAdmin}
              isUserMember={association.isUserMember}
              address={`${association.address.city}, ${association.address.state}`}
              join={joinAssociation}
              isUserPending={association.isUserPending}
              manageText={t('manage')}
              membersLink={
                association.isUserAdmin &&
                `/associations/${association.id}/members`
              }
              editLink={`/associations/${association.id}/members`}
              organizationId={association.id}
              isJoinBlocked={isJoinBlocked}
              blockedMessage={blockedMessage}
            />
          ) : (
            <MobileView
              toggleShareModal={toggleShareModal}
              name={association.name}
              memberCount={association.memberCount}
              memberPreview={association.memberPreview}
              badgeUrl={association.logoImage}
              coverUrl={association.coverImage}
              description={association.description}
              isUserAdmin={association.isUserAdmin}
              isUserMember={association.isUserMember}
              address={`${association.address.city}, ${association.address.state}`}
              join={joinAssociation}
              isUserPending={association.isUserPending}
              manageText={t('manage')}
              membersLink={
                association.isUserAdmin &&
                `/associations/${association.id}/members`
              }
              editLink={`/associations/${association.id}/edit`}
              organizationId={association.id}
              isJoinBlocked={isJoinBlocked}
              blockedMessage={blockedMessage}
            />
          )}
        </FadeIn>
      </div>
      <MoreOptionsModal
        toggleShareModal={toggleShareModal}
        toggleMoreOptions={toggleMoreOptionsModal}
        visible={isMoreOptionsModalOpen}
      />
      <ShareModal
        visible={isShareModalOpen}
        onClose={toggleShareModal}
        shareLink={`${config.SITE_URL}associations/${association.id}`}
      />
      {isEmailDomainModalVisible && (
        <EmailDomainModal
          emailDomains={association.requiredEmailDomains}
          onClose={closeEmailDomainModal}
          association={association}
        />
      )}
    </>
  );
};

export default ViewAssociation;
